/**
 * BrandedLoginHeader
 *
 * Renders the company name with "Powered by Adrsta AI" right-aligned
 * underneath — always flush with the last character of the company name.
 *
 * Props:
 * - companyName   (string)          : Customer's company name. Required.
 * - accentColor   (string)          : Hex color for the spark icon. Default: #6C47FF
 * - theme         ('light'|'dark')  : Default: 'light'
 * - poweredByText (string)          : Override the badge label.
 *
 * Usage:
 * <BrandedLoginHeader companyName="NBCUniversal" accentColor="#1D2D82" />
 */

interface AdrstaSpark {
  size?: number;
  color?: string;
}

function AdrstaSpark({ size = 9, color = "#6C47FF" }: AdrstaSpark) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M8 1 L9.2 6.8 L15 8 L9.2 9.2 L8 15 L6.8 9.2 L1 8 L6.8 6.8 Z"
        fill={color}
        opacity="0.85"
      />
      <circle cx="13" cy="3" r="1.1" fill={color} opacity="0.5" />
    </svg>
  );
}

interface BrandedLoginHeaderProps {
  companyName?: string;
  accentColor?: string;
  theme?: "light" | "dark";
  poweredByText?: string;
}

export default function BrandedLoginHeader({
  companyName = "Your Company",
  accentColor = "#6C47FF",
  theme = "light",
  poweredByText = "Powered by Adrsta AI",
}: BrandedLoginHeaderProps) {
  const isDark = theme === "dark";
  const textPrimary = isDark ? "rgba(255,255,255,0.95)" : "rgba(0,0,0,0.88)";
  const textMuted = isDark ? "rgba(255,255,255,0.30)" : "rgba(0,0,0,0.30)";

  return (
    /*
     * `inline-block` shrink-wraps to the company name width so the
     * "Powered by" badge stays flush under the last character.
     */
    <div style={{ display: "inline-block" }}>
      <div
        style={{
          fontSize: "1.4rem",
          fontWeight: 800,
          letterSpacing: "-0.03em",
          lineHeight: 1.1,
          color: textPrimary,
          whiteSpace: "nowrap",
          fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        }}
      >
        {companyName}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "4px",
          marginTop: "2px",
          fontSize: "0.55rem",
          fontWeight: 500,
          color: textMuted,
          letterSpacing: "0.015em",
          fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        }}
      >
        <AdrstaSpark size={9} color={accentColor} />
        {poweredByText}
      </div>
    </div>
  );
}
